import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Mono, Text, Button } from "@athena/ui";
import { TvMinimalPlay, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { VideoSelectorModal } from "./VideoSelectorModal";
import { CaptionEditor } from "./CaptionEditor";
import { UpdateVideoContentDocument } from "../../graphql/courses.generated";
import { SetContentTagsDocument } from "../../graphql/media.generated";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";

export interface CourseVideoBuilderProps {
    contentId: string;
    organizationId: string;
    onTitleChange?: (title: string) => void;
    onSave?: <T>(fn: () => Promise<T>) => Promise<T>;
    toggleShown?: (hidden: boolean, hasUploaded: boolean) => void;
}

export function CourseVideoBuilder({
    contentId,
    organizationId,
    onTitleChange,
    onSave,
    toggleShown,
}: CourseVideoBuilderProps) {
    const { t } = useTranslation();
    const [
        selectorOpen,
        setSelectorOpen,
    ] = useState<boolean>(false);
    const [
        title,
        setTitle,
    ] = useState<string>("");
    const [
        uploadedAssetId,
        setUploadedAssetId,
    ] = useState<string | null>(null);

    const { isPlatformAdmin, organizationId: viewerOrganizationId } = usePlatformAdmin();

    const [updateVideo] = useMutation(UpdateVideoContentDocument);
    /*
     * Tags live on the `content` row rather than on either the asset or the video specialization,
     * so they are set by id here rather than folded into the update input below.
     */
    const [setContentTags] = useMutation(SetContentTagsDocument);

    const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    function scheduleSave(
        newTitle: string,
        videoSource: { videoUrl?: string; videoAssetId?: string },
        /** What the selector's upload form described, if it was used. Omitted when replacing a source. */
        described: { description?: string; brands?: string[] } = {},
    ) {
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(() => {
            const mutate = () =>
                updateVideo({
                    variables: {
                        id: contentId,
                        input: {
                            title: newTitle || undefined,
                            ...videoSource,
                            ...(described.description ? { description: described.description } : {}),
                            // An empty list clears brands, so only send one when the operator chose.
                            ...(described.brands?.length ? { brands: described.brands } : {}),
                        },
                    },
                });
            onSave ? onSave(mutate) : mutate();
            if (newTitle) onTitleChange?.(newTitle);
        }, 800);
    }

    return (
        <div className="flex flex-col gap-4">
            <button
                type="button"
                onClick={() => setSelectorOpen(true)}
                className="bg-card border-border hover:bg-muted/40 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 transition-colors"
            >
                <div className="flex h-12 w-12 items-center justify-center rounded bg-pink-400/10">
                    <TvMinimalPlay className="h-6 w-6 text-pink-400" />
                </div>
                <Text className="text-primary text-md font-display font-semibold">{t("admin.lms.chooseVideo")}</Text>
                <Text className="text-muted-foreground text-sm">{t("admin.lms.chooseVideoSubtitle")}</Text>
            </button>
            <VideoSelectorModal
                open={selectorOpen}
                onOpenChange={setSelectorOpen}
                // Listing scope is the viewer's reach (their own org, or every org for a platform
                // admin) — matching Library and Data, see usePlatformAdmin. The course's own org
                // is only where a *new* upload from this modal is placed, via uploadOrganizationId
                // below; scoping the listing to it too meant a video uploaded anywhere else (e.g.
                // from Library, as a platform admin) never showed up here even though it was
                // clearly visible there.
                organizationId={viewerOrganizationId}
                uploadOrganizationId={organizationId}
                onUpload={(uploaded) => {
                    setUploadedAssetId(uploaded.videoAssetId);
                    if (!title) setTitle(uploaded.title);
                    setSelectorOpen(false);
                    scheduleSave(
                        title || uploaded.title,
                        { videoAssetId: uploaded.videoAssetId },
                        { description: uploaded.description, brands: uploaded.brands },
                    );
                    /*
                     * Not debounced with the rest: `setContentTags` replaces the whole set, so
                     * there is nothing for a later keystroke to coalesce with, and running it
                     * through the same timer would make an empty selection look like a clear.
                     */
                    if (uploaded.tagIds.length > 0) {
                        const tagMutate = () => setContentTags({ variables: { contentId, tagIds: uploaded.tagIds } });
                        onSave ? onSave(tagMutate) : tagMutate();
                    }
                }}
                isPlatformAdmin={isPlatformAdmin}
            />

            {uploadedAssetId && (
                <div className="bg-card overflow-hidden rounded-lg border p-4">
                    <Mono size="xs" muted className="tracking-widest uppercase">
                        {t("admin.lms.captions")}
                    </Mono>
                    <div className="mt-3">
                        <CaptionEditor assetId={uploadedAssetId} />
                    </div>
                </div>
            )}
            {toggleShown && (
                <div className="flex justify-end">
                    <Button onClick={() => toggleShown(false, Boolean(uploadedAssetId))} variant={"outline"}>
                        <X
                            aria-label={t("common.actions.close")}
                            className="text-muted-foreground h-4 w-4 shrink-0 cursor-pointer rounded-xl"
                        />{" "}
                        Close
                    </Button>
                </div>
            )}
        </div>
    );
}
